import { useCallback, useReducer } from "react";
import { GridData } from "../../components/ag-grid-extensions/models/GridData";
import { GridApi } from "ag-grid-community";
import { ActionMenuItem } from "../../components/action-menu/ActionMenuItem";
import { actions } from "./UserPermissionsReportActions";
import { sortAndDispatchActions } from "../../common/utilities/SortAndDispatchActions";

export type UserPermissionsReportStateType = {

    organizationID: number | undefined;
    data: GridData | undefined;
    gridApi: GridApi | undefined;
    downloadUid: string | undefined;
    actions: ActionMenuItem[]
};

const defaultUserPermissionsReportState: UserPermissionsReportStateType = {
    
    organizationID: undefined,
    data: undefined,
    gridApi: undefined,
    downloadUid: undefined,
    actions: actions
};

export {defaultUserPermissionsReportState}

enum UserPermissionsReportStateActionType {
    OrganizationChanged = 'USER_PERMISSIONS_ORGANIZATION_CHANGED',
    DataChanged = 'USER_PERMISSIONS_REPORT_DATA_CHANGED',
    //LoadingChanged = 'USER_PERMISSIONS_REPORT_LOADING_CHANGED',
    DownloadUidChanged = 'USER_PERMISSIONS_REPORT_DOWNLOAD_UID_CHANGED',
    GridApiChanged = 'USER_PERMISSIONS_REPORT_GRID_API_CHANGED',
    ActionsChanged = 'USER_PERMISSIONS_REPORT_ACTIONS_CHANGED'

}

type UserPermissionsReportStateActions = | {
    type: UserPermissionsReportStateActionType.DataChanged;
    payload: GridData;
} | {
    type: UserPermissionsReportStateActionType.GridApiChanged;
    payload: GridApi;
} | {
    type: UserPermissionsReportStateActionType.OrganizationChanged;
    payload: number | undefined;
} | {
    type: UserPermissionsReportStateActionType.DownloadUidChanged;
    payload: string | undefined;
} | {
    type: UserPermissionsReportStateActionType.ActionsChanged;
    payload: ActionMenuItem[];
};

function userPermissionsReportStateReducer(state: UserPermissionsReportStateType, 
    action: UserPermissionsReportStateActions): UserPermissionsReportStateType {

    const copy = {...state};
    switch (action.type) {
        
        case UserPermissionsReportStateActionType.OrganizationChanged:
            copy.organizationID = action.payload;
            return copy;

        case UserPermissionsReportStateActionType.DataChanged:
            copy.data = action.payload;
            return copy;

        case UserPermissionsReportStateActionType.GridApiChanged:
            copy.gridApi = action.payload;
            return copy;

    
        case UserPermissionsReportStateActionType.DownloadUidChanged:
            copy.downloadUid = action.payload;
            return copy;

        case UserPermissionsReportStateActionType.ActionsChanged:
            copy.actions = action.payload;
            return copy;
    
        default:
            return state;
    }
}

export default function useUserPermissionsReportState() {

    const [state, dispatch] = useReducer(userPermissionsReportStateReducer, {...defaultUserPermissionsReportState});

    const setData = useCallback((value: GridData) => dispatch({
        type: UserPermissionsReportStateActionType.DataChanged,
        payload: value
    }), []);

    const setGridApi = useCallback((value: GridApi) => dispatch({
        type: UserPermissionsReportStateActionType.GridApiChanged,
        payload: value
    }), []);

    const setDownloadUid = useCallback((value: string | undefined) => dispatch({
        type: UserPermissionsReportStateActionType.DownloadUidChanged,
        payload: value
    }), []);

    const setOrganization = useCallback((value: number | undefined) => dispatch({
        type: UserPermissionsReportStateActionType.OrganizationChanged,
        payload: value
    }), []);

    // const setActions = useCallback((value: ActionMenuItem[]) => dispatch({
    //     type: UserPermissionsReportStateActionType.ActionsChanged,
    //     payload: value
    // }), []);

    const setActions = useCallback((actions: ActionMenuItem[]) => sortAndDispatchActions(
        state.actions,
        actions,
        (sortedActions) => dispatch({
            type: UserPermissionsReportStateActionType.ActionsChanged,
            payload: sortedActions
        })
    ), [state.actions]);

    return {
        state,
        setData,
        setGridApi,
        setDownloadUid,
        setOrganization,
        setActions
    };
}

